import { Injectable } from "@angular/core";
import { poe1, poe2 } from "../../../game";
import { Comparator, Rarity } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Filter, CustomRule, AdvancedConditions, FlaskFilter } from "../../models/filter-models";
import { RuleGeneratorService } from "./rule-generator.service";
import { GenerationHelperService } from "./generation-helper.service";
import { ItemDataService } from "../item-data.service";
import { getUtilityPlusTopLifeManaFlaskTypes } from "./poe1-crafting-bases-generator.service";

@Injectable({
  providedIn: 'root'
})
export class FlaskCharmGeneratorService {
  constructor(
    private ruleFormatter: RuleGeneratorService,
    private helper: GenerationHelperService,
    private itemDataService: ItemDataService,
  ) {
  }

  public generateHighILevelCharmsRules(filter: Filter): string {
    if (poe1 || !filter.showHighILevelCharms) return '';

    return this.ruleFormatter.generateEquipmentRule(
      EquipmentType.OtherCharmsILevel,
      new CustomRule({
        itemClass: 'Charms',
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Normal,
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showHighILevelCharmsMinILevel || 82 }),
        baseTypes: (!filter.showHighILevelCharmTypes || filter.showHighILevelCharmTypes.includes('All')) ? undefined : filter.showHighILevelCharmTypes,
      }),
      filter,
      'High Item Level Charms'
    );
  }

  public generateHighILevelFlasksRules(filter: Filter): string {
    if (poe2 || !filter.showHighILevelFlasks) return '';

    const flaskClasses = poe1 ? '"Life Flasks" "Mana Flasks" "Hybrid Flasks" "Utility Flasks" "Tinctures"' : '"Life Flasks" "Mana Flasks"';
    const baseTypes = (!filter.showHighILevelFlaskTypes || filter.showHighILevelFlaskTypes.includes('All'))
      ? (poe1 ? getUtilityPlusTopLifeManaFlaskTypes(this.itemDataService.getItemData()).map(b => b.name) : undefined)
      : filter.showHighILevelFlaskTypes;

    return this.ruleFormatter.generateEquipmentRule(
      EquipmentType.OtherFlasksILevel,
      new CustomRule({
        rarityComparator: poe1 ? Comparator.Equal : Comparator.GreaterThanOrEqual,
        rarity: Rarity.Normal,
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showHighILevelFlasksMinILevel || 84 }),
        baseTypes: baseTypes,
      }),
      filter,
      'High iLevel Flasks',
      { customClass: flaskClasses }
    );
  }

  public generateMyFlaskRules(filter: Filter): string {
    let rules = '';

    if (!filter.flaskFilters || filter.flaskFilters.length === 0) {
      return '';
    }

    // Group flaskFilters by flaskClass
    const groupedByClass: { [key: string]: FlaskFilter[] } = {};
    filter.flaskFilters
      .filter(f => f.show)
      .forEach(flask => {
        if (!groupedByClass[flask.flaskClass]) {
          groupedByClass[flask.flaskClass] = [];
        }
        groupedByClass[flask.flaskClass].push(flask);
      });

    const classOrder = ['Life Flasks', 'Mana Flasks', 'Hybrid Flasks', 'Utility Flasks', 'Tinctures', 'Charms'];

    for (const flaskClass of classOrder) {
      if (!groupedByClass[flaskClass]) continue;

      const flasks = groupedByClass[flaskClass];

      flasks.forEach(flask => {
        const originalIndex = filter.flaskFilters.findIndex(f => f === flask);
        const baseTypes = (!flask.flaskBaseTypes || flask.flaskBaseTypes.includes('All')) ? ['All'] : flask.flaskBaseTypes;

        // Generate highlight rules for quality (if enabled)
        if (flask.highlightConfig.quality) {
          rules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyFlasksQuality,
            new CustomRule({
              itemClass: flask.flaskClass,
              advancedConditions: Object.assign(this.buildAdvancedConditions(flask), {
                minItemLevel: flask.itemLevel,
                minQuality: flask.highlightConfig.qualityMin ?? flask.quality ?? flask.minQuality ?? 15,
              }),
              baseTypes: baseTypes,
            }),
            filter,
            `Favourite Quality ${flaskClass}`,
            undefined,
            undefined,
            undefined,
            'flask',
            originalIndex
          ) + '\n\n';
        }

        // Generate highlight rules for iLevel (if enabled)
        if (flask.highlightConfig.iLevel) {
          rules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyFlasksILevel,
            new CustomRule({
              itemClass: flask.flaskClass,
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              advancedConditions: Object.assign(this.buildAdvancedConditions(flask), {
                minItemLevel: flask.highlightConfig.iLevelMin ?? flask.itemLevel ?? 82,
              }),
              baseTypes: baseTypes,
            }),
            filter,
            `Favourite Item Level ${flaskClass}`,
            undefined,
            undefined,
            undefined,
            'flask',
            originalIndex
          ) + '\n\n';
        }

        // Generate show rules for regularNormal (if enabled and rarity is Normal)
        if (flask.highlightConfig.regularNormal && flask.rarity === Rarity.Normal) {
          rules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyFlasksRegularNormal,
            new CustomRule({
              itemClass: flask.flaskClass,
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              baseTypes: baseTypes,
              advancedConditions: Object.assign(this.buildAdvancedConditions(flask), {
                minItemLevel: flask.itemLevel,
                minQuality: flask.quality ?? flask.minQuality,
              }),
            }),
            filter,
            `Favourite Regular Normal ${flaskClass}`,
            undefined,
            undefined,
            undefined,
            'flask',
            originalIndex
          ) + '\n\n';
        }

        // Generate show rules for regularMagic (if enabled and rarity is Magic or Normal)
        if (flask.highlightConfig.regularMagic && (flask.rarity === Rarity.Magic || flask.rarity === Rarity.Normal)) {
          rules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyFlasksRegularMagic,
            new CustomRule({
              itemClass: flask.flaskClass,
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Magic,
              baseTypes: baseTypes,
              advancedConditions: Object.assign(this.buildAdvancedConditions(flask), {
                minItemLevel: flask.itemLevel,
                minQuality: flask.quality ?? flask.minQuality,
              }),
            }),
            filter,
            `Favourite Regular Magic ${flaskClass}`,
            undefined,
            undefined,
            undefined,
            'flask',
            originalIndex
          ) + '\n\n';
        }
      });
    }

    return rules.trim();
  }

  private buildAdvancedConditions(flask: FlaskFilter): AdvancedConditions {
    const advancedConditions = new AdvancedConditions();

    advancedConditions.corrupted = flask.corrupted;
    advancedConditions.minQuality = flask.minQuality;

    return advancedConditions;
  }

  public generateQualityFlaskRules(filter: Filter): string {
    if (!filter.showQualityFlasks) return '';

    const flaskClasses = poe1
      ? '"Life Flasks" "Mana Flasks" "Hybrid Flasks" "Utility Flasks" "Tinctures"'
      : '"Life Flasks" "Mana Flasks"';

    return this.ruleFormatter.generateEquipmentRule(
      EquipmentType.OtherFlasksQuality,
      new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityFlasksMinQuality || 1 }),
      }),
      filter,
      'Quality Flasks',
      { customClass: flaskClasses }
    );
  }

  public generateQualityCharmsRules(filter: Filter): string {
    if (poe1 || !filter.showQualityCharms) return '';

    // Use OtherFlasksQuality for charms as well since there's no separate EquipmentType for charm quality
    return this.ruleFormatter.generateEquipmentRule(
      EquipmentType.OtherCharmsQuality,
      new CustomRule({
        itemClass: 'Charms',
        advancedConditions: new AdvancedConditions({ minQuality: filter.showQualityCharmsMinQuality || 1 }),
      }),
      filter,
      'Quality Charms',
    );
  }
}
